/**
 * StorageManager - Manages storage quota and cleanup policies
 *
 * Enforces 5GB max storage limit for offline books.
 * Monitors browser storage quota.
 * Implements automatic cleanup when approaching limits.
 *
 * Features:
 * - 5GB app-level limit (configurable)
 * - Browser quota monitoring via Storage API
 * - Warning at 80% usage
 * - Automatic cleanup of least recently accessed books
 * - Persistent storage request (prevents automatic eviction)
 */

import { offlineDatabase } from './OfflineDatabase';
import type {
  StorageInfo,
  StorageQuota,
  StorageCleanupResult,
  OfflineBook,
} from '@/types/offline';

export class StorageManager {
  // 5GB max storage (in bytes)
  private readonly MAX_STORAGE_BYTES = 5 * 1024 * 1024 * 1024; // 5GB
  private readonly WARNING_THRESHOLD = 0.8; // 80%
  private readonly CLEANUP_TARGET_PERCENT = 0.6; // Clean up to 60% usage

  /**
   * Get current storage information
   */
  async getStorageInfo(): Promise<StorageInfo> {
    // Get browser storage estimate
    let browserUsage = 0;
    let browserQuota = 0;

    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        browserUsage = estimate.usage || 0;
        browserQuota = estimate.quota || 0;
      } catch (error) {
        console.warn('Failed to get storage estimate:', error);
      }
    }

    // Get our app's offline storage usage
    const offlineBooksSize = await offlineDatabase.getTotalStorageUsed();
    const offlineBooksCount = await offlineDatabase.books.count();

    const percentUsed = (offlineBooksSize / this.MAX_STORAGE_BYTES) * 100;

    return {
      usage: browserUsage,
      quota: browserQuota,
      percentUsed,
      offlineBooksCount,
      offlineBooksSize,
    };
  }

  /**
   * Get storage quota information
   */
  async getStorageQuota(): Promise<StorageQuota> {
    const info = await this.getStorageInfo();

    return {
      maxBytes: this.MAX_STORAGE_BYTES,
      usedBytes: info.offlineBooksSize,
      availableBytes: this.MAX_STORAGE_BYTES - info.offlineBooksSize,
      warningThresholdPercent: this.WARNING_THRESHOLD * 100,
    };
  }

  /**
   * Check if there's enough space to download a book
   */
  async canDownloadBook(bookSizeBytes: number): Promise<{
    canDownload: boolean;
    reason?: string;
    needsCleanup: boolean;
    suggestedCleanupBytes?: number;
  }> {
    const quota = await this.getStorageQuota();
    const afterDownloadSize = quota.usedBytes + bookSizeBytes;

    if (afterDownloadSize <= quota.maxBytes) {
      return { canDownload: true, needsCleanup: false };
    }

    // Calculate how much we need to free
    const suggestedCleanupBytes = afterDownloadSize - quota.maxBytes;

    return {
      canDownload: false,
      reason: `Not enough storage. Need to free ${this.formatBytes(suggestedCleanupBytes)}.`,
      needsCleanup: true,
      suggestedCleanupBytes,
    };
  }

  /**
   * Check if storage is approaching limit (>80%)
   */
  async isApproachingLimit(): Promise<boolean> {
    const info = await this.getStorageInfo();
    return info.percentUsed >= this.WARNING_THRESHOLD * 100;
  }

  /**
   * Request persistent storage (prevent automatic eviction)
   */
  async requestPersistentStorage(): Promise<boolean> {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        const isPersisted = await navigator.storage.persist();
        if (isPersisted) {
          console.log('✅ Storage persisted - will not be automatically cleared');
        } else {
          console.warn('⚠️ Storage not persisted - may be cleared under storage pressure');
        }
        return isPersisted;
      } catch (error) {
        console.error('Failed to request persistent storage:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Check if storage is persisted
   */
  async isStoragePersisted(): Promise<boolean> {
    if ('storage' in navigator && 'persisted' in navigator.storage) {
      try {
        return await navigator.storage.persisted();
      } catch (error) {
        console.error('Failed to check if storage is persisted:', error);
        return false;
      }
    }
    return false;
  }

  /**
   * Cleanup old content to free space
   * Deletes least recently accessed books until target is reached
   */
  async cleanupOldContent(targetFreeBytes: number): Promise<StorageCleanupResult> {
    const books = await offlineDatabase.getBooksOldestFirst();
    const deletedBooks: string[] = [];
    let freedBytes = 0;

    for (const book of books) {
      if (freedBytes >= targetFreeBytes) {
        break;
      }

      // Skip recently accessed books (< 7 days)
      const daysSinceAccess = this.daysSince(book.lastAccessed);
      if (daysSinceAccess < 7) {
        continue;
      }

      // Delete the book
      await offlineDatabase.deleteBook(book.id);
      deletedBooks.push(book.id);
      freedBytes += book.sizeBytes;

      console.log(`🗑️ Deleted offline book: ${book.title} (${this.formatBytes(book.sizeBytes)})`);
    }

    return {
      freedBytes,
      deletedBooks,
    };
  }

  /**
   * Automatic cleanup to target usage (60%)
   */
  async autoCleanup(): Promise<StorageCleanupResult> {
    const quota = await this.getStorageQuota();
    const targetUsageBytes = this.MAX_STORAGE_BYTES * this.CLEANUP_TARGET_PERCENT;
    const bytesToFree = Math.max(0, quota.usedBytes - targetUsageBytes);

    if (bytesToFree === 0) {
      return { freedBytes: 0, deletedBooks: [] };
    }

    console.log(`🧹 Auto-cleanup: Freeing ${this.formatBytes(bytesToFree)} to reach ${this.CLEANUP_TARGET_PERCENT * 100}% usage`);

    return this.cleanupOldContent(bytesToFree);
  }

  /**
   * Get books suggested for deletion (oldest accessed first)
   */
  async getSuggestedBooksForDeletion(count: number = 5): Promise<OfflineBook[]> {
    const books = await offlineDatabase.getBooksOldestFirst();
    return books.slice(0, count);
  }

  /**
   * Delete specific book by ID
   */
  async deleteBook(bookId: string): Promise<void> {
    await offlineDatabase.deleteBook(bookId);
    console.log(`🗑️ Deleted offline book: ${bookId}`);
  }

  /**
   * Delete multiple books
   */
  async deleteBooks(bookIds: string[]): Promise<StorageCleanupResult> {
    let freedBytes = 0;
    const deletedBooks: string[] = [];

    for (const bookId of bookIds) {
      const book = await offlineDatabase.getBook(bookId);
      if (book) {
        await offlineDatabase.deleteBook(bookId);
        deletedBooks.push(bookId);
        freedBytes += book.sizeBytes;
      }
    }

    return { freedBytes, deletedBooks };
  }

  /**
   * Get storage statistics
   */
  async getStorageStats(): Promise<{
    totalBooks: number;
    totalSize: string;
    percentUsed: number;
    availableSpace: string;
    oldestBook?: { title: string; daysSinceAccess: number };
    newestBook?: { title: string; daysSinceDownload: number };
  }> {
    const books = await offlineDatabase.getAllBooks();
    const quota = await this.getStorageQuota();

    let oldestBook: OfflineBook | undefined;
    let newestBook: OfflineBook | undefined;

    for (const book of books) {
      if (!oldestBook || book.lastAccessed < oldestBook.lastAccessed) {
        oldestBook = book;
      }
      if (!newestBook || book.downloadedAt > newestBook.downloadedAt) {
        newestBook = book;
      }
    }

    return {
      totalBooks: books.length,
      totalSize: this.formatBytes(quota.usedBytes),
      percentUsed: (quota.usedBytes / quota.maxBytes) * 100,
      availableSpace: this.formatBytes(quota.availableBytes),
      oldestBook: oldestBook ? {
        title: oldestBook.title,
        daysSinceAccess: this.daysSince(oldestBook.lastAccessed),
      } : undefined,
      newestBook: newestBook ? {
        title: newestBook.title,
        daysSinceDownload: this.daysSince(newestBook.downloadedAt),
      } : undefined,
    };
  }

  /**
   * Helper: Calculate days since a date
   */
  private daysSince(date: Date): number {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  /**
   * Helper: Format bytes to human-readable string
   */
  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Helper: Format percentage
   */
  formatPercent(value: number): string {
    return `${Math.round(value)}%`;
  }
}

// Singleton instance
export const storageManager = new StorageManager();
